import type { FacetCount } from '../../src/api/traffic-queries';
import { UNSPECIFIED_COUNTY } from '../../src/wire';
import { countyPopulation } from './population';

/**
 * The Tabloo modules' arithmetic. Both formats here draw bars whose *length* is the claim, so the
 * scaling is a fact about the data and not a styling detail — it lives in one tested place rather
 * than in a `style` attribute halfway down a component.
 */

/** Raw counts crown the biggest county; per 1000 people flips it. Shipping one without the other
 * would be picking the answer, so the toggle offers both (ux-spec §5 row 3). */
export type LeaderboardMode = 'total' | 'perCapita';

export interface LeaderboardRow {
  key: string;
  /** Its place among the counties, or `null` for the unstated bucket, which ranks at nothing. */
  rank: number | null;
  /** The figure on the chosen scale: a count, or accidents per 1000 residents. */
  value: number;
  /** The bar's length as a fraction of the longest bar drawn, `0`…`1`. */
  share: number;
}

/**
 * The leaderboard's rows, ranked on the chosen scale, with the unstated bucket kept last.
 *
 * The `UNSPECIFIED` row survives every path (FacetBar rule 3): it is a measurement of the corpus —
 * mostly roads crossing several counties — and dropping it would renormalise the board onto the
 * accidents that happened to name a place. It carries no rank because it is not a county, and it is
 * never ranked per capita because it has no population to divide by.
 *
 * A county the population table has no figure for is dropped from the per-capita board rather than
 * ranked against a guessed denominator; on the raw board it is a real count and stays.
 */
export function leaderboardRows(
  counties: readonly FacetCount[],
  mode: LeaderboardMode,
  limit: number,
): LeaderboardRow[] {
  const unstated = counties.find((c) => c.key === UNSPECIFIED_COUNTY) ?? null;

  const scaled = counties.flatMap((c) => {
    if (c.key === UNSPECIFIED_COUNTY) return [];
    if (mode === 'total') return [{ key: c.key, value: c.count }];
    const people = countyPopulation(c.key);
    return people === null || people <= 0
      ? []
      : [{ key: c.key, value: (c.count / people) * 1_000 }];
  });

  const ranked = scaled.sort((a, b) => b.value - a.value).slice(0, limit);

  // Bars are measured against the longest bar on the *same* scale: the unstated row has no
  // population, so per capita it is drawn against the raw counts rather than against a ratio.
  const rawMax = Math.max(0, ...counties.map((c) => c.count));
  const longest = Math.max(
    ...ranked.map((r) => r.value),
    unstated !== null && mode === 'total' ? unstated.count : 0,
  );
  const ratio = (value: number, against: number): number =>
    against > 0 ? Math.min(1, value / against) : 0;
  const share = (value: number): number => ratio(value, longest);

  const rows: LeaderboardRow[] = ranked.map((r, i) => ({
    key: r.key,
    rank: i + 1,
    value: r.value,
    share: share(r.value),
  }));

  if (unstated !== null) {
    rows.push({
      key: UNSPECIFIED_COUNTY,
      rank: null,
      value: unstated.count,
      share: mode === 'total' ? share(unstated.count) : ratio(unstated.count, rawMax),
    });
  }

  return rows;
}

export interface SparkBar {
  key: string;
  count: number;
  /** Height as a fraction of the peak, `0`…`1`. */
  share: number;
}

/** The 24 hour bars and the tallest of them. `peak` is `null` when nothing was counted: a peak of
 * zero would put a label on an hour that had no accidents in it. */
export function sparkbars(hours: readonly FacetCount[]): {
  bars: SparkBar[];
  peak: FacetCount | null;
} {
  const top = hours.reduce<FacetCount | null>(
    (best, h) => (best === null || h.count > best.count ? h : best),
    null,
  );
  const peak = top === null || top.count <= 0 ? null : top;
  const bars = hours.map((h) => ({
    key: h.key,
    count: h.count,
    share: peak === null ? 0 : h.count / peak.count,
  }));
  return { bars, peak };
}
